import { applyPatch } from 'fast-json-patch';
import produce from 'immer';
import { get, isEmpty } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';

import { RaceHeader } from '@swf/casino';
import { Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import Translation from '@swf/components/Translation/Translation';
import { raceEntity } from '@swf/core/analytics';
import { request } from '@swf/core/api/request';
import { Header } from '@swf/core/enums';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { CasinoRace, RaceFilter, RaceService } from '@swf/core/types/CasinoRaces';
import { Status } from '@swf/core/types/CasinoRaces';
import type { WidgetInstanceProps } from '@swf/core/types/Widget';
import { WidgetComponent } from '@swf/core/types/Widget';
import type { TranslationTag } from '@swf/core/types/i18n';
import { isCompleted } from '@swf/core/utils/casino';
import type {
  CasinoRacesWidgetConfig,
  CasinoRacesWidgetServices
} from '@swf/generated/types/widgets/CasinoRacesWidget';

import {
  TXTWCL_My_Races,
  TXTWCL_Next_Races,
  TXTWCL_We_are_out_of_fuel,
  TXTWCL_We_are_taking_a_short_stop_to__ELL
} from './CasinoRacesWidget.messages.json';
import styles from './CasinoRacesWidget.scss';
import CompletedGrid from './components/CompletedGrid/CompletedGrid';
import LeaderboardModal from './components/LeaderboardModal/LeaderboardModal';
import RacesGrid from './components/RacesGrid/RacesGrid';

interface Data {
  all?: { races: CasinoRace[] };
  user?: CasinoRace[];
  userCompleted?: CasinoRace[];
}

type OwnProps = WidgetInstanceProps<CasinoRacesWidgetConfig, CasinoRacesWidgetServices, Data>;

type StateProps = ReturnType<typeof mapState>;

export type Props = OwnProps & StateProps;

interface JoinMeta {
  id: string;
  service: string;
}

export interface State {
  active: TranslationTag['id'];
  leaderRace?: CasinoRace['id'];
  leaderboard?: CasinoRace['leaderboard'];
}

class CasinoRacesWidget extends WidgetComponent<Props, State> {
  static serviceReducers = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    join: (state: State, { data, meta = {} }: Record<string, any>) =>
      produce(state, (draft) => {
        const { id, service } = meta as JoinMeta;
        const updateRace = draft[service].find((race: CasinoRace) => id === race.id);
        if (updateRace) {
          updateRace.userRaceStanding = data;
          updateRace.numOfPlayers++;
        }
        return draft;
      })
  };

  static trackingReducer(services: CasinoRacesWidgetServices, data: Data) {
    return data?.all?.races?.map(raceEntity);
  }

  service: RaceService = this.getService();
  scrollOffset = 0;
  state: State = { active: TXTWCL_Next_Races.id };
  ukgcTermsAccepted = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  racesEmpty = (races: any[]) => isEmpty(races) || races.every((race) => isCompleted(race.status));

  handleFilter = ({ tag, service }: RaceFilter) => {
    this.service = service;
    this.fetchRaces();
    this.setState({ active: tag.id });
  };

  handleClose = () => this.setState({ leaderRace: undefined });

  componentDidMount() {
    this.updateSubscriptions();
  }

  async componentDidUpdate(lastProps: Props, lastState: State) {
    const { isAuthenticated, isPlayMoney, currency } = this.props;
    const { active } = this.state;
    const races = this.getRaces();
    const lastRaces = this.getRaces(lastProps);
    const authChanged = lastProps.isAuthenticated !== isAuthenticated;

    if (active !== lastState.active || races !== lastRaces) {
      this.updateSubscriptions();
    } else if (authChanged) {
      this.handleFilter({ tag: TXTWCL_Next_Races, service: 'all' });
    } else if (
      isPlayMoney !== lastProps.isPlayMoney ||
      (!authChanged && currency !== lastProps.currency)
    ) {
      this.fetchRaces();
    }
  }

  showLeaderboard = (race: CasinoRace) => async () => {
    const leaderboard =
      race.status === Status.COMPLETED ? race.leaderboard : await this.fetchLeaderboard(race);
    this.scrollOffset = window.pageYOffset;
    this.setState({ leaderRace: race.id, leaderboard });
  };

  makeReducer =
    (id: CasinoRace['id']) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: State, { data }: any) =>
      produce(state, (draft) => {
        const updateRace = this.getRaces().find((race) => race.id === id);
        if (updateRace) {
          if (updateRace.id === this.state.leaderRace) {
            this.fetchLeaderboard(updateRace).then((leaderboard) => this.setState({ leaderboard }));
          }
          if (data[0].path === '/status') {
            this.fetchRaces();
          }
          applyPatch(updateRace, data);
        }
      });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userReducer = (state: State, { data }: any) =>
    produce(state, (draft) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data.forEach((patch: any) => {
        const updateRace = this.getRaces().find((race) => race.id === patch.value.raceId);
        if (updateRace) {
          applyPatch(updateRace, [patch]);
        }
      });
    });

  getRaces = (props = this.props) =>
    get(
      props.data,
      this.service === 'all' ? `${this.service}.races` : this.service,
      []
    ) as CasinoRace[];

  isRacesLoaded = () => get(this.props.data, this.service);

  getCompleted = () => get(this.props.data, 'userCompleted', []) as CasinoRace[];

  fetchRaces = () => this.props.fetchService(this.service);

  fetchCompleted = () => this.props.fetchService('userCompleted');

  fetchLeaderboard = async ({ id }: CasinoRace) => {
    try {
      const { body } = await request({
        ...this.props.options,
        service: 'casino.races.leaderboard',
        body: { raceId: id }
      });
      return body;
    } catch (e) {
      // do nothing
    }
  };

  updateSubscriptions() {
    const { props, service } = this;
    props.updateSubscriptions(
      'race',
      this.getRaces().map(({ id }) => ({
        service,
        templateValues: { id },
        reducer: this.makeReducer(id)
      }))
    );
    if (props.isAuthenticated) {
      props.updateSubscriptions('userRaces', [{ service, reducer: this.userReducer }]);
    }
  }

  getService(): RaceService {
    return Object.keys(this.props.services).find((service) => {
      const {
        config: { onAuth, onLoad }
      } = this.props.services[service];
      return onAuth || onLoad;
    }) as RaceService;
  }

  render() {
    const {
      isAuthenticated,
      style,
      config,
      services: { user }
    } = this.props;
    const { active } = this.state;
    const races = this.getRaces();
    const isLoaded = this.isRacesLoaded();
    const racesEmpty = this.racesEmpty(races);
    const isMyRaces = active === TXTWCL_My_Races.id;
    const raceList = [...this.getRaces(), ...this.getCompleted()];
    const raceIndex = raceList.findIndex(({ id }) => id === this.state.leaderRace);
    const race = raceList[raceIndex];
    const showFilters = isAuthenticated && user?.config.onAuth === true;

    if (config.hideWhenEmpty && racesEmpty) {
      return null;
    }

    return (
      <section className={styles.container} style={style}>
        <Vary by={[Header.Country, Header.Currency]} />
        <RaceHeader
          logo={config.logo}
          help={config.help}
          helpButton={config.helpModalButton}
          helpTitle={config.helpTitle}
          moreLink={config.moreLink}
          active={active}
          handleFilter={this.handleFilter}
          showFilters={showFilters}
          legacyWidget
        />
        {!!race && (
          <LeaderboardModal
            race={race}
            leaderboard={this.state.leaderboard}
            onClose={this.handleClose}
            widgetId={this.props.id}
            path={
              this.service === 'all'
                ? `${this.service}.races[${raceIndex}]`
                : this.service === 'user'
                ? `${this.service}[${raceIndex}]`
                : this.service
            }
          />
        )}
        {racesEmpty && !isMyRaces && isLoaded ? (
          <div className={styles.noRaces}>
            <span className={styles.warningTitle}>
              <Translation {...TXTWCL_We_are_out_of_fuel} />
            </span>
            <span className={styles.warningContext}>
              <Translation {...TXTWCL_We_are_taking_a_short_stop_to__ELL} />
            </span>
          </div>
        ) : (
          <RacesGrid
            races={races}
            onLeaderboard={this.showLeaderboard}
            leaderboardVisible={config.leaderboardVisible}
            widgetId={this.props.id}
            path={this.service === 'all' ? `${this.service}.races` : this.service}
          />
        )}
        {isMyRaces && isAuthenticated && (
          <CompletedGrid
            races={this.getCompleted()}
            fetchCompleted={this.fetchCompleted}
            showLeaderboard={this.showLeaderboard}
          />
        )}
        {/* eslint-disable @typescript-eslint/no-explicit-any */}
      </section>
    );
  }
}

const mapState = (rootState: RootState, { id }: OwnProps) => ({
  currency: select.currency.get(rootState),
  isAuthenticated: select.session.isAuthenticated(rootState),
  isPlayMoney: select.currency.isPlayMoney(rootState),
  isDesktopEmbedded: select.site.isDesktopEmbedded(rootState),
  options: select.site.getRequestOptions(rootState)
});

// @ts-ignore https://github.com/DefinitelyTyped/DefinitelyTyped/issues/31363
export default connect(mapState)(CasinoRacesWidget) as ComponentType<OwnProps>;
